import { BlogPostData, BlogPostListItem } from './types';
import { handleApiResponse } from '../common';
import { getApiBaseUrl, API_VERSION } from '../index';

/**
 * Blog API client
 * Follows the same patterns as spacesApi
 */
export const blogApi = {
  /**
   * List published posts (for public display) - includes full content
   * @returns {Promise<BlogPostData[]>}
   */
  async listPublished() {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/blog/posts`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();

      if (!Array.isArray(data)) {
        console.error('Unexpected data format:', data);
        throw new Error('Invalid data format received from API');
      }

      return BlogPostData.fromApiResponseList(data);
    } catch (error) {
      console.error('Failed to fetch published posts:', error);
      throw error;
    }
  },

  /**
   * List all posts including drafts (for admin)
   * @returns {Promise<BlogPostListItem[]>}
   */
  async listAll() {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/blog/posts/all`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();

      if (!Array.isArray(data)) {
        console.error('Unexpected data format:', data);
        throw new Error('Invalid data format received from API');
      }

      return BlogPostListItem.fromApiResponseList(data);
    } catch (error) {
      console.error('Failed to fetch all posts:', error);
      throw error;
    }
  },

  /**
   * Get a single post by ID
   * @param {number} postId
   * @returns {Promise<BlogPostData>}
   */
  async get(postId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/blog/posts/${postId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      return BlogPostData.fromApiResponse(data);
    } catch (error) {
      console.error(`Failed to fetch post ${postId}:`, error);
      throw error;
    }
  },

  /**
   * Get a single post by slug
   * @param {string} slug
   * @returns {Promise<BlogPostData>}
   */
  async getBySlug(slug) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/blog/posts/slug/${slug}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      return BlogPostData.fromApiResponse(data);
    } catch (error) {
      console.error(`Failed to fetch post by slug ${slug}:`, error);
      throw error;
    }
  },

  /**
   * Create a new blog post
   * @param {Object} postData - Post creation data
   * @returns {Promise<BlogPostData>}
   */
  async create(postData) {
    try {
      const payload = postData instanceof BlogPostData 
        ? postData.toCreateFormat() 
        : postData;

      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/blog/posts`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      return BlogPostData.fromApiResponse(data);
    } catch (error) {
      console.error('Failed to create post:', error);
      throw error;
    }
  },

  /**
   * Update an existing post
   * @param {number} postId
   * @param {Object} updates - Partial post data to update
   * @returns {Promise<BlogPostData>}
   */
  async update(postId, updates) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/blog/posts/${postId}`, {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(updates)
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      return BlogPostData.fromApiResponse(data);
    } catch (error) {
      console.error(`Failed to update post ${postId}:`, error);
      throw error;
    }
  },

  /**
   * Delete a post
   * @param {number} postId
   * @returns {Promise<{message: string}>}
   */
  async delete(postId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/blog/posts/${postId}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const validatedResponse = await handleApiResponse(response);
      return await validatedResponse.json();
    } catch (error) {
      console.error(`Failed to delete post ${postId}:`, error);
      throw error;
    }
  },

  /**
   * Publish a post
   * @param {number} postId
   * @returns {Promise<BlogPostData>}
   */
  async publish(postId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/blog/posts/${postId}/publish`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      return BlogPostData.fromApiResponse(data);
    } catch (error) {
      console.error(`Failed to publish post ${postId}:`, error);
      throw error;
    }
  },

  /**
   * Unpublish a post
   * @param {number} postId
   * @returns {Promise<BlogPostData>}
   */
  async unpublish(postId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/blog/posts/${postId}/unpublish`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      return BlogPostData.fromApiResponse(data);
    } catch (error) {
      console.error(`Failed to unpublish post ${postId}:`, error);
      throw error;
    }
  }
};

